/**
 * Migrates localStorage data when a user's ID changes (e.g., Firebase ID → UUID migration).
 *
 * This handles the User ID migration where user IDs change from Firebase UIDs to UUIDs.
 * All localStorage keys that include the user ID need to be migrated to use the new ID.
 */

// Key to store the last known user ID (not keyed by user ID since that's what changes)
const LAST_KNOWN_USER_ID_KEY = 'lastKnownUserId'

// All localStorage key prefixes that include user ID
const USER_KEYED_PREFIXES = [
  'recentSearches',
  'lastViewedTab',
  'lastViewedPivotTable',
  'hasEnteredDossbotCode',
  'floatingWidgetCorner',
  'recentPages',
  'configbaseNavOverride',
] as const

/**
 * Detects if a user's ID has changed and migrates their localStorage data.
 *
 * This should be called on app load after the user is authenticated.
 * It's idempotent and safe to call multiple times.
 *
 * @param currentUserId - The user's current ID from the API (new UUID)
 */
export function migrateUserLocalStorage(currentUserId: string): void {
  const lastKnownUserId = localStorage.getItem(LAST_KNOWN_USER_ID_KEY)

  // First time user is logging in, or ID hasn't changed
  if (!lastKnownUserId || lastKnownUserId === currentUserId) {
    // Update the last known user ID
    localStorage.setItem(LAST_KNOWN_USER_ID_KEY, currentUserId)
    return
  }

  // Migrate all user-keyed localStorage entries
  for (const prefix of USER_KEYED_PREFIXES) {
    const oldKey = `${prefix}_${lastKnownUserId}`
    const newKey = `${prefix}_${currentUserId}`

    const oldValue = localStorage.getItem(oldKey)
    if (oldValue !== null) {
      // Copy to new key
      localStorage.setItem(newKey, oldValue)
      // Remove old key
      localStorage.removeItem(oldKey)
    }
  }

  // Update the last known user ID to the new one
  localStorage.setItem(LAST_KNOWN_USER_ID_KEY, currentUserId)
}

/**
 * Clears all user-specific localStorage data for a given user ID.
 * Useful for testing or manual cleanup.
 *
 * @param userId - The user ID to clear data for
 */
export function clearUserLocalStorage(userId: string): void {
  for (const prefix of USER_KEYED_PREFIXES) {
    const key = `${prefix}_${userId}`
    localStorage.removeItem(key)
  }
}
